import React, { useEffect, useState } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useAuth } from '@/shared/contexts/AuthContext';
import { INSTITUTION_KEYS } from '@/shared/constants/institutions';
import guidelinesService from '@/shared/services/guidelinesService';
import { renderMarkdown } from '@/shared/utils/markdown';
import { serverMessage } from '@/shared/utils/serverMessage';
import usePageTitle from '@/shared/hooks/usePageTitle';

/**
 * What a reader with no profile sees: there is no institution to merge for, so
 * the shared section comes first and then every institution's, in registry
 * order. No heading is added, because the member merge adds none either: each
 * institution's section opens with its own, which is what tells a member
 * reading the merge whose rules follow. An empty section is left out, as the
 * merge leaves it out.
 */
function allSectionsText(guidelines) {
    return [
        guidelines.shared,
        ...INSTITUTION_KEYS.map((key) => guidelines.byInstitution[key]),
    ]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join('\n\n');
}

function GuidelinesPage() {
    usePageTitle('Reuse guidelines');
    const { userProfile } = useAuth();
    const isAdmin = (userProfile?.roles || []).includes('admin');

    const [guidelines, setGuidelines] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        guidelinesService
            .get()
            .then((data) => {
                if (!cancelled) {
                    setGuidelines(data);
                }
            })
            .catch((caught) => {
                if (!cancelled) {
                    setError(
                        serverMessage(
                            caught,
                            'Could not load the reuse guidelines.'
                        )
                    );
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading guidelines" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <h1 className="h3 mb-3">Reuse guidelines</h1>
            <div
                dangerouslySetInnerHTML={{
                    __html: renderMarkdown(
                        guidelines.forMe
                            ? guidelines.forMe.text
                            : allSectionsText(guidelines)
                    ),
                }}
            />
            <p className="text-muted small mt-4">
                <time dateTime={guidelines.updatedAt}>
                    Updated{' '}
                    {new Date(guidelines.updatedAt).toLocaleDateString()}
                </time>
            </p>
            {isAdmin && <Link to="/admin/guidelines">Edit guidelines</Link>}
        </Container>
    );
}

export default GuidelinesPage;
